import React, { useMemo, useState } from "react";

import makeStyles from "@material-ui/styles/makeStyles";
// material-ui
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Link from "@material-ui/core/Link";

//icon
import HistoryIcon from "@material-ui/icons/History";
import ShowIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";

import { PageHeader, translate as translateHOC } from "jazasoft";

// local
import MuiTable from "../../components/MuiTable";
import HistoryDialog from "../../components/HistoryDialog";
import EditDialog from "./EditDialog";
import ViewDialog from "./ViewDialog";

// helper
import useSettings from "./useSetting";
import { isEmpty, isTrue, truncate } from "../../utils/helpers";
import SettingKeys from "../../utils/SettingKeys";
import { Role } from "../../utils/types";

const DialogName = {
  EDIT: "edit",
  VIEW: "view",
  HISTORY: "history"
};

const useStyles = makeStyles(_ => ({
  root: {
    margin: "1.5em"
  }
}));

const Setting = props => {
  const classes = useStyles();
  const { roles, translate, i18nKey, history } = props;
  const { settings, groups, updateSetting } = useSettings(props);
  // state
  const [expanded, setExpanded] = useState("proman");
  const [dialogState, setDialogState] = useState({ active: false, name: null, data: undefined }); // name - edit, history

  const columns = useMemo(
    () => [
      { dataKey: "name", title: "Setting" },
      { dataKey: "resource", title: "Resource" },
      { dataKey: "description", title: "Description" },
      {
        dataKey: "value",
        title: "Value",
        render: (value, _, row) => {
          if (row.dataType === "boolean") return isTrue(value) ? "Yes" : "No";

          const displayValue = `${value || ""}${row.unit || ""}`;
          if (displayValue.length <= 50) return displayValue;

          return (
            <span>
              {truncate(displayValue, 50)}{" "}
              <Link
                component="button"
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  setDialogState({ active: true, name: DialogName.VIEW, data: row });
                }}>
                View more
              </Link>
            </span>
          );
        }
      }
    ],
    []
  );

  const onEditSubmit = values => {
    updateSetting(values, () => setDialogState({}));
  };

  const onClose = () => {
    setDialogState({ active: false });
  };

  const rows = !isEmpty(expanded)
    ? settings.filter(
        setting =>
          !setting.hidden &&
          setting.groupId === expanded &&
          setting.roles &&
          setting.roles.split(",").filter(e => roles.includes(e.trim()) || roles.includes(Role.IT)).length > 0
      )
    : [];

  const onInlineActionClick = (e, action, row, _) => {
    if (action === "show") {
      if (row.dataType === "select-multiple" || row.dataType === "text-json" || row.dataType === "validation") {
        setDialogState({ active: true, name: DialogName.VIEW, data: row });
      }
    } else if (action === "editRow") {
      if (row.groupId === SettingKeys.ENTITY_FIELD_VALIDATION) {
        history.push(`settings/${row.id}/view`, { settingResource: row.resource });
      } else {
        setDialogState({
          active: true,
          name: DialogName.EDIT,
          data: row
        });
      }
    } else if (action === "history") {
      setDialogState({ active: true, name: DialogName.HISTORY, data: row });
    }
  };

  const inlineActions = roles?.includes(Role.MASTER)
    ? [
        { name: "show", tooltip: translate("js.action.show", { _: "Show" }), icon: <ShowIcon /> },
        { name: "editRow", tooltip: translate("js.action.edit", { _: "Edit" }), icon: <EditIcon /> },
        { name: "history", tooltip: translate("js.action.history", { _: "History" }), icon: <HistoryIcon /> }
      ]
    : [];

  const translatedGroups = groups?.map(e => ({ ...e, name: translate(`resources.settings.title.${e.id}`, { _: e.name }) })) || [];

  return (
    <div>
      <PageHeader title="resources.settings.pageHeader.home" titleArgs={{ _: "Settings" }} />

      <EditDialog
        open={(!isEmpty(dialogState) && dialogState.active && dialogState.name === DialogName.EDIT) || false}
        setting={dialogState.data}
        onClose={onClose}
        onSubmit={onEditSubmit}
      />
      <ViewDialog
        open={(!isEmpty(dialogState) && dialogState.active && dialogState.name === DialogName.VIEW) || false}
        setting={dialogState.data}
        onClose={onClose}
      />
      <HistoryDialog
        open={(!isEmpty(dialogState) && dialogState.active && dialogState.name === DialogName.HISTORY) || false}
        onClose={onClose}
        url={dialogState.name === DialogName.HISTORY && dialogState.data ? `settings/${dialogState.data.key}/logs` : null}
        params={{ resource: dialogState?.data?.resource }}
        i18nKeyPrefix={`resources.${i18nKey}.history`}
      />

      <div className={classes.root}>
        {!isEmpty(translatedGroups) &&
          translatedGroups.map(group => (
            <Accordion key={group.id} expanded={expanded === group.id} onChange={(_, isExpanded) => setExpanded(isExpanded ? group.id : false)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} id={group.id}>
                <Typography className={classes.heading}>{group.name}</Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails style={{ flexDirection: "column", padding: 0 }}>
                <MuiTable
                  key={JSON.stringify(rows)}
                  columns={columns}
                  rows={rows}
                  i18nKey="resources.settings.fields"
                  fontSize={16}
                  cellLength={50}
                  inlineActions={inlineActions}
                  onInlineActionClick={onInlineActionClick}
                  comparator={(a, b) => a.serialNo - b.serialNo}
                />
              </AccordionDetails>
            </Accordion>
          ))}
      </div>
    </div>
  );
};

export default translateHOC(Setting);
